"use client"

import * as React from "react"
import clsx from "clsx"
import Link from "next/link"
import { X, Search, ChevronDown } from "lucide-react"
import type { Header as HeaderType } from "@/payload-types"
import { CMSLink } from "@/components/Link"
import { Logo } from "@/components/Logo/Logo"

type LinkBlock = { blockType: "linkItem"; link: any }
type MegaMenuBlock = {
  blockType: "megaMenu"
  label: string
  description?: string
  columns?: Array<{ heading?: string; items?: Array<{ link: any }> }>
}

export const HeaderMobileNav: React.FC<{
  id?: string
  open: boolean
  onClose: () => void
  data: HeaderType
}> = ({ id, open, onClose, data }) => {
  const blocks = (data as any)?.navItems ?? []

  // lock scroll when open
  React.useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  return (
    <div
      id={id ?? "mobile-nav"}
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      inert={!open}
      className={clsx(
        "md:hidden fixed inset-0 z-[90] transition",
        open ? "pointer-events-auto" : "pointer-events-none"
      )}
    >
      {/* Backdrop */}
      <div
        className={clsx(
          "absolute inset-0 bg-black/40 transition-opacity",
          open ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
      />

      {/* PANEL: full height, slides from right */}
      <div
        className={clsx(
          "absolute inset-y-0 right-0 w-full max-w-[480px] shadow-xl",
          "transition-transform duration-300 will-change-transform",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="h-full flex flex-col bg-minigreen-7 text-white">
          {/* Top bar: Search (left) • Logo (center) • X (right) */}
          <div className="sticky top-0 z-10 grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-4 border-b border-white/10 bg-minigreen-7">
            {/* Search (left) */}
            <Link
              href="/search"
              aria-label="Search"
              onClick={onClose}
              className="h-10 w-10 grid place-items-center rounded-full border-2 border-minigreen-2/70 text-white/90"
            >
              <Search size={18} strokeWidth={3} />
            </Link>

            {/* Logo (center) */}
            <div className="justify-self-center">
              <Link href="/" aria-label="Home" onClick={onClose} className="block">
                <Logo className="h-7 w-auto text-minigreen-1" />
              </Link>
            </div>

            {/* Close (right) — no outline/border */}
            <button
              type="button"
              aria-label="Close menu"
              onClick={onClose}
              className="h-10 w-10 grid place-items-center rounded-full text-white/90 focus:outline-none"
            >
              <X size={18} strokeWidth={3} />
            </button>
          </div>

          {/* Content */}
          <nav className="flex-1 overflow-y-auto px-5 py-5">
            <ul className="space-y-5">
              {blocks.map((block: LinkBlock | MegaMenuBlock, i: number) => (
                <React.Fragment key={i}>
                  {block.blockType === "linkItem" ? (
                    <li>
                      <CMSLink
                        {...(block as LinkBlock).link}
                        appearance="link"
                        className="block rounded-md px-3 py-3 text-lg font-semibold hover:underline"
                        onClick={onClose}
                      />
                    </li>
                  ) : (
                    <MobileAccordion
                      block={block as MegaMenuBlock}
                      onNavigate={onClose}
                    />
                  )}

                  {/* Separator line between sections (not after the last) */}
                  {i < blocks.length - 1 && (
                    <div className="-mx-5 my-5 h-px bg-white/10" />
                  )}
                </React.Fragment>
              ))}
            </ul>

            {/* Donate button */}
            <div className="pt-8">
              <Link
                href="https://secure.qgiv.com/for/mininaturereserve"
                onClick={onClose}
                className="block text-center rounded-full px-5 py-3 font-semibold tracking-wide bg-minigreen-2 text-mini-primary hover:bg-minigreen-1"
              >
                DONATE
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </div>
  )
}

const MobileAccordion: React.FC<{
  block: MegaMenuBlock
  onNavigate: () => void
}> = ({ block, onNavigate }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <li className="rounded-lg">
      {/* Top-level label */}
      <button
        type="button"
        className="w-full flex items-center justify-between rounded-md px-3 py-3 text-left text-lg font-semibold hover:underline"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        {block.label}
        <ChevronDown
          className={clsx("size-5 transition-transform", open && "rotate-180")}
        />
      </button>

      {/* Drawer: darker background ONLY under revealed content; full panel width via negative margins */}
      <div className={clsx(open ? "block" : "hidden")}>
        <div className="bg-black/10 -mx-5 mt-6 -mb-5 px-5 pt-6 pb-4 rounded-b-lg">
        {/* <div className="bg-black/10 -mx-5 -mt-16 pt-20 px-5 pb-4 rounded-b-lg"> */}
          {block.description && (
            <div className="px-3 pb-6">
              <p className="text-sm text-white/80">
                {block.description}
              </p>
              <div className="mt-4 h-px bg-white/10" />
            </div>
          )}



          {(block.columns || []).map((col, i) => (
            <div key={i} className="pb-4">
              {/* Subtitle (align with main title) */}
              {col.heading && (
                <div className="pl-3 pr-2 py-1 text-xs uppercase tracking-wider text-white/60">
                  {col.heading}
                </div>
              )}

              {/* Links: indented */}
              <ul className="mt-1 space-y-4">
                {(col.items || []).map((it, j) => (
                  <li key={j} className="pl-6">
                    <CMSLink
                      {...it.link}
                      appearance="link"
                      className="block rounded-md px-2 py-2 underline"
                      onClick={onNavigate}
                    />
                  </li>
                ))}
              </ul>

              {/* section separator inside an accordion column (optional, subtle) */}
              {i < (block.columns?.length || 0) - 1 && (
                <div className="mt-4 h-px bg-white/10" />
              )}
            </div>
          ))}
        </div>
      </div>
    </li>
  )
}
